import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { type AuthState } from '@/common/types/auth.types';
import { type UserIncidentsType } from '@/common/types/incidents.types';

const baseURL = import.meta.env.VITE_APP_INCIDENTS_API;

export type UserActiveIncidentsResponse = {
  pvSettingID: number;
  pvAccountID: number;
  pvSettingType: string;
  pvSettingValue: string; // JSON string of incident IDs, e.g. "[2771,2792,2812]"
  pvVoid: number;
  pvEntryDate: string;
  pvGlobalSetting: number;
  pvGroupID: number | null;
};

export type ActiveIncidentsResponse = {
  count: number;
  dataset: UserIncidentsType[];
};

export const incidentsApi = createApi({
  reducerPath: 'incidentsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${baseURL}/api`,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as AuthState).auth.jwtToken;
      if (token) {
        headers.set('Authorization', token);
      }
      return headers;
    },
  }),
  endpoints: (builder) => ({
    getUserActiveIncidents: builder.query<UserActiveIncidentsResponse | null, void>({
      queryFn: async (_arg, { getState }, _extraOptions, baseQuery) => {
        const token = (getState() as AuthState).auth.jwtToken;
        const userId = (getState() as AuthState).auth.userId;
        const result = await baseQuery({
          url: '/settings',
          params: {
            access_token: token,
            pvAccountID: userId,
            pvSettingType: 'selectedIncidents',
          },
        });
        // API returns an array, extract first item
        const data = result.data as UserActiveIncidentsResponse[] | UserActiveIncidentsResponse;
        const setting = Array.isArray(data) ? data[0] : data;
        return { data: setting ?? null };
      },
    }),
    getAllActiveIncidents: builder.query<ActiveIncidentsResponse, void>({
      queryFn: async (_arg, { getState }, _extraOptions, baseQuery) => {
        const state = getState() as AuthState;
        const groupID = state.auth.activeCountyId ?? state.auth.tokenVerification?.pvActiveGroupID ?? 1;
        // Make the request with the groupID
        const result = await baseQuery({
          url: '/incidents',
          params: {
            groupID: groupID,
          },
        });
        return result as { data: ActiveIncidentsResponse };
      },
    }),
  }),
});

export const {
  useLazyGetUserActiveIncidentsQuery,
  useGetUserActiveIncidentsQuery,
  useLazyGetAllActiveIncidentsQuery,
  useGetAllActiveIncidentsQuery,
} = incidentsApi;
